var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext } from "react";
import { TestTag, getAutomationTag } from "../../common/testAutomation";
import { MultiSelectFilter } from "./MultiSelectFilter";
import { FilterContext, updateFilters, } from "../../providers/FilterProvider";
import { useGetClientDetailsQuery, useGetClientPlatformQuery, useGetDataCustomFiltersQuery, useGetLeaderProfileAttributes, } from "../../store/api/core";
import { Stack } from "@eightfold.ai/octuple";
import { Additional } from "./Additional";
import { AvailableFilters, dataFilters, getEntity } from "../../common/filters";
import { filterOverrides } from "./overrides";
import { FilterRestrictionContext } from "../../providers/FilterRestrictionProvider";
import { useClient } from "../../hooks/client";
import { hasFeatureFlag } from "../../common/featureFlags";
import { FeatureFlag } from "../../../config/application";
export var AdditionalAvailableFilters = new Set([
    AvailableFilters.RetentionRisk,
    AvailableFilters.CareerInterests,
    AvailableFilters.AssessmentSource,
    AvailableFilters.AgeRange,
    AvailableFilters.DerailerRisk,
    AvailableFilters.WillingToRelocate,
    AvailableFilters.Gender,
    AvailableFilters.Race,
    AvailableFilters.Ninebox,
    AvailableFilters.NineboxRatings,
    AvailableFilters.Assessed,
    AvailableFilters.CareerTrack,
    AvailableFilters.SecurityClearance,
    AvailableFilters.Retirement,
    AvailableFilters.LossImpact,
]);
var hasAdditionalFilters = function (filters) {
    return Array.from(filters).some(function (filter) { return AdditionalAvailableFilters.has(filter); });
};
export var DefaultFilterSet = new Set([
    AvailableFilters.Locations,
    AvailableFilters.LocationCountry,
    AvailableFilters.BusinessUnits,
    AvailableFilters.Division,
    AvailableFilters.BusinessFunctions,
    AvailableFilters.BusinessSubFunctions,
    AvailableFilters.Roles,
    AvailableFilters.JobLevels,
    AvailableFilters.Managers,
    AvailableFilters.Reset,
    AvailableFilters.Spinner,
].concat(Array.from(AdditionalAvailableFilters)));
export var CohortFilterSet = new Set([
    AvailableFilters.Project,
    AvailableFilters.Locations,
    AvailableFilters.LocationCountry,
    AvailableFilters.BusinessUnits,
    AvailableFilters.Division,
    AvailableFilters.BusinessFunctions,
    AvailableFilters.Roles,
    AvailableFilters.JobLevels,
    AvailableFilters.Managers,
    AvailableFilters.GenderPrimary,
    AvailableFilters.PeoplePartner,
    AvailableFilters.ProfileType,
    AvailableFilters.SelectedForTheRole,
    AvailableFilters.AssessmentJourney,
    AvailableFilters.Reset,
    AvailableFilters.Spinner,
]);
export var LandingPageFilters = new Set([
    AvailableFilters.Locations,
    AvailableFilters.LocationCountry,
    AvailableFilters.BusinessUnits,
    AvailableFilters.Division,
    AvailableFilters.Roles,
    AvailableFilters.GenderPrimary,
    AvailableFilters.Spinner,
    AvailableFilters.Reset,
    AvailableFilters.Ninebox,
].concat(Array.from(AdditionalAvailableFilters)));
export var CohortInsightsPageFilters = new Set([
    // Use this set for new Intelligence page
    AvailableFilters.BusinessUnits,
    AvailableFilters.Locations,
    AvailableFilters.LocationCountry,
    AvailableFilters.Division,
    AvailableFilters.BusinessFunctions,
    AvailableFilters.BusinessSubFunctions,
    AvailableFilters.Roles,
    AvailableFilters.JobLevels,
    AvailableFilters.Managers,
    AvailableFilters.Reset,
    AvailableFilters.Spinner,
]);
export var SuccessProfileFilterSet = new Set([
    AvailableFilters.BusinessFunctionSuccessProfile,
    AvailableFilters.BusinessSubFunctionSuccessProfile,
    AvailableFilters.JobLevelSuccessProfile,
    AvailableFilters.RoleName,
    AvailableFilters.Spinner,
    AvailableFilters.Reset,
]);
var filterOrder = [
    AvailableFilters.Project,
    AvailableFilters.Locations,
    AvailableFilters.LocationCountry,
    AvailableFilters.BusinessUnits,
    AvailableFilters.Division,
    AvailableFilters.BusinessFunctions,
    AvailableFilters.BusinessFunctionSuccessProfile,
    AvailableFilters.BusinessSubFunctions,
    AvailableFilters.BusinessSubFunctionSuccessProfile,
    AvailableFilters.Roles,
    AvailableFilters.JobLevels,
    AvailableFilters.JobLevelSuccessProfile,
    AvailableFilters.Managers,
    AvailableFilters.RoleName,
    AvailableFilters.GenderPrimary,
    AvailableFilters.PeoplePartner,
    AvailableFilters.SelectedForTheRole,
    AvailableFilters.ProfileType,
    AvailableFilters.AssessmentJourney,
    AvailableFilters.Spinner,
    AvailableFilters.Reset,
];
export var getFiltersAsArray = function (order, availableFilters) {
    return function (_a) {
        var clearFilters = _a.clearFilters, disableFilters = _a.disableFilters;
        return order
            .filter(function (key) { return availableFilters.has(key); })
            .map(function (key) { return ({
            key: key,
            implementation: filterOverrides[key] ? (filterOverrides[key]({ clearFilters: clearFilters, disableFilters: disableFilters })) : (_jsx(MultiSelectFilter, { entity: dataFilters[key].entity, label: dataFilters[key].label, disableFilters: disableFilters }, "filter-".concat(key))),
        }); });
    };
};
var useFilterComponents = function (_a) {
    var clearFilters = _a.clearFilters, availableFilters = _a.availableFilters, disableFilters = _a.disableFilters;
    return getFiltersAsArray(filterOrder, availableFilters)({ clearFilters: clearFilters, disableFilters: disableFilters });
};
var RightSideFilters = new Set([
    AvailableFilters.Spinner,
    AvailableFilters.Reset,
]);
var isRightSideFilter = function (_a) {
    var key = _a.key;
    return RightSideFilters.has(key);
};
var isLeftSideFilter = function (_a) {
    var key = _a.key;
    return !isRightSideFilter({ key: key });
};
var implementationalize = function (_a) {
    var implementation = _a.implementation;
    return implementation;
};
var appendTrailer = function (implementations, trailer) {
    if (!trailer) {
        return implementations;
    }
    if (Array.isArray(trailer)) {
        return implementations.concat(trailer);
    }
    return implementations.concat([trailer]);
};
var DeiHidden = new Set([
    AvailableFilters.Gender,
    AvailableFilters.GenderPrimary,
    AvailableFilters.Race,
]);
var isBaseEntity = function (entity) {
    return [AvailableFilters.Project, AvailableFilters.AssessmentJourney]
        .map(getEntity)
        .includes(entity);
};
export var useDynamicOptions = function () {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var getFilters = useContext(FilterContext).getFilters;
    var client = useClient();
    var restrictions = useContext(FilterRestrictionContext).restrictions;
    var order = useGetLeaderProfileAttributes({});
    var data = ((_a = useGetDataCustomFiltersQuery({ filters: getFilters() }, {
        shouldSkipCall: function () { return !client.clientId; },
    }).data) === null || _a === void 0 ? void 0 : _a.data) || [];
    if (!((_b = order === null || order === void 0 ? void 0 : order.data) === null || _b === void 0 ? void 0 : _b.data)) {
        return {
            useOptionsQuery: function () { return ({ data: { data: {} } }); },
            options: {},
            clearFilters: function () { },
            orderedFilters: [],
            order: undefined,
        };
    }
    var rawOptions = data.reduce(function (current, _a) {
        var _b;
        var filterKey = _a.filterKey, filterOptions = _a.filterOptions;
        return (__assign(__assign({}, current), (_b = {}, _b[filterKey] = filterOptions, _b)));
    }, {});
    var lookup = ((_d = (_c = order === null || order === void 0 ? void 0 : order.data) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.reduce(function (current, _a) {
        var _b;
        var code = _a.code, displayOrder = _a.displayOrder, isComparable = _a.isComparable, isReportable = _a.isReportable;
        return (__assign(__assign({}, current), (_b = {}, _b[code] = {
            order: displayOrder,
            heatmapable: isComparable,
            reportable: isReportable,
        }, _b)));
    }, {})) ||
        {};
    var useOptionsQuery = function () { return ({ data: { data: rawOptions } }); };
    var options = (data === null || data === void 0 ? void 0 : data.reduce(function (current, _a) {
        var _b;
        var _c, _d;
        var filterKey = _a.filterKey, filterLabel = _a.filterLabel, filterOptions = _a.filterOptions;
        var useOptions = function () { return filterOptions; };
        return __assign(__assign({}, current), (_b = {}, _b[filterKey] = {
            options: filterOptions,
            useOptions: useOptions,
            label: filterLabel,
            entity: filterKey,
            heatmappable: isBaseEntity(filterKey) || ((_c = lookup[filterKey]) === null || _c === void 0 ? void 0 : _c.heatmapable) || false,
            reportable: isBaseEntity(filterKey) || ((_d = lookup[filterKey]) === null || _d === void 0 ? void 0 : _d.reportable) || false,
        }, _b));
    }, {})) || {};
    var projectEntity = getEntity(AvailableFilters.Project);
    var ajEntity = getEntity(AvailableFilters.AssessmentJourney);
    var excludedEntities = new Set([projectEntity, ajEntity]);
    var orderedFilters = __spreadArray(__spreadArray(__spreadArray([], (((_f = (_e = options[projectEntity]) === null || _e === void 0 ? void 0 : _e.options) === null || _f === void 0 ? void 0 : _f.length)
        ? [options[projectEntity]]
        : []), true), (((_h = (_g = options[ajEntity]) === null || _g === void 0 ? void 0 : _g.options) === null || _h === void 0 ? void 0 : _h.length) ? [options[ajEntity]] : []), true), Object.values(options)
        .filter(function (_a) {
        var entity = _a.entity, opts = _a.options;
        return (opts === null || opts === void 0 ? void 0 : opts.length) && !excludedEntities.has(entity);
    })
        .sort(function (_a, _b) {
        var _c, _d;
        var a = _a.entity;
        var b = _b.entity;
        if (!((_c = lookup[a]) === null || _c === void 0 ? void 0 : _c.order) || !((_d = lookup[b]) === null || _d === void 0 ? void 0 : _d.order)) {
            return a.toString() < b.toString() ? -1 : 1;
        }
        return lookup[a].order - lookup[b].order;
    }), true);
    var clearFilters = function () {
        updateFilters(__assign({}, Object.keys(options).reduce(function (current, key) {
            var _a;
            var _b;
            return (__assign(__assign({}, current), (((_b = restrictions[key]) === null || _b === void 0 ? void 0 : _b.has(key)) ? {} : (_a = {}, _a[key] = [], _a))));
        }, {})));
    };
    return { useOptionsQuery: useOptionsQuery, options: options, clearFilters: clearFilters, orderedFilters: orderedFilters, order: order };
};
export var DynamicFilters = function (_a) {
    var leftTrailer = _a.leftTrailer, rightTrailer = _a.rightTrailer, disableFilters = _a.disableFilters;
    var _b = useDynamicOptions(), useOptionsQuery = _b.useOptionsQuery, orderedFilters = _b.orderedFilters;
    var left = appendTrailer(orderedFilters.map(function (_a) {
        var entity = _a.entity, label = _a.label;
        return (_jsx(MultiSelectFilter, { entity: entity, label: label, disableFilters: disableFilters, useOptionsQuery: useOptionsQuery }, "filter-".concat(entity)));
    }), leftTrailer);
    var right = appendTrailer([
        filterOverrides[AvailableFilters.Spinner]({ disableFilters: true }),
        filterOverrides[AvailableFilters.Reset]({
            disableFilters: disableFilters,
        }),
    ], rightTrailer);
    return _jsx(StyledFilters, { left: left, right: right });
};
export var isCohort = function () { var _a, _b; return !!((_b = (_a = window.location) === null || _a === void 0 ? void 0 : _a.pathname) === null || _b === void 0 ? void 0 : _b.includes("/cohort-insights/")); };
export var Filters = function (props) {
    return isCohort() && hasFeatureFlag(FeatureFlag.DynamicFilters) ? (_jsx(DynamicFilters, __assign({}, props))) : (_jsx(StaticFilters, __assign({}, props)));
};
var StaticFilters = function (_a) {
    var _b;
    var availableFiltersRaw = _a.availableFilters, clearFilters = _a.clearFilters, leftTrailer = _a.leftTrailer, rightTrailer = _a.rightTrailer, disableFilters = _a.disableFilters;
    var hideGenderEthnicity = (_b = useGetClientDetailsQuery({}).data) === null || _b === void 0 ? void 0 : _b.data.hide_gender_and_ethnicity;
    var availableFilters = Array.from(availableFiltersRaw || new Set()).reduce(function (current, key) {
        if (hideGenderEthnicity === true && DeiHidden.has(key)) {
            return current;
        }
        return current.add(key);
    }, new Set());
    var filters = useFilterComponents({
        clearFilters: clearFilters,
        availableFilters: availableFilters || DefaultFilterSet,
        disableFilters: disableFilters,
    });
    var filtersVisibleValues = useGetClientPlatformQuery({});
    var presentFilters = new Set(availableFilters || DefaultFilterSet);
    var isUsedFilter = function (_a) {
        var _b, _c, _d, _e;
        var key = _a.key;
        var filters = (_e = (_d = (_c = (_b = filtersVisibleValues === null || filtersVisibleValues === void 0 ? void 0 : filtersVisibleValues.data) === null || _b === void 0 ? void 0 : _b.data[0]) === null || _c === void 0 ? void 0 : _c.applications) === null || _d === void 0 ? void 0 : _d.orgDashboard) === null || _e === void 0 ? void 0 : _e.filters;
        if (!presentFilters.has(key)) {
            return false;
        }
        var isFilterVisible = (filters === null || filters === void 0 ? void 0 : filters[key]) !== false;
        // Special handling: If subFunction is true, show both subFunction and function filters
        if ((filters === null || filters === void 0 ? void 0 : filters.subFunction) === true) {
            if (key === "function" || key === "subFunction") {
                return true;
            }
        }
        return isFilterVisible;
    };
    var left = appendTrailer(filters
        .filter(isUsedFilter)
        .filter(isLeftSideFilter)
        .map(implementationalize), leftTrailer).filter(function (v) { return v; });
    var right = appendTrailer(filters
        .filter(isUsedFilter)
        .filter(isRightSideFilter)
        .map(implementationalize)
        .filter(function (v) { return v; })
        .concat(hasAdditionalFilters(availableFilters || new Set())
        ? [_jsx(Additional, { filters: availableFilters || new Set() })]
        : []), rightTrailer);
    return _jsx(StyledFilters, { left: left, right: right });
};
var StyledFilters = function (_a) {
    var left = _a.left, right = _a.right;
    return (_jsxs(Stack, __assign({ style: { zIndex: 2 }, fullWidth: true, direction: "horizontal", justify: "space-between" }, getAutomationTag(TestTag.FiltersBar), { children: [_jsx(Stack, __assign({ direction: "horizontal", flexGap: "s", wrap: "wrap" }, { children: left })), _jsx(Stack, __assign({ direction: "horizontal", flexGap: "s", wrap: "nowrap" }, { children: right }))] })));
};
